import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import AdminSidebar from '@/pages/admin/dashboard/components/AdminSidebar';

interface Application {
  id: string;
  name: string;
  email: string;
  phone: string;
  message: string;
  status: string;
  created_at: string;
  volunteer_opportunities: { title_en: string } | null;
}

const STATUS_OPTIONS = ['pending', 'reviewed', 'accepted', 'rejected'];

export default function AdminVolunteersPage() {
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [selected, setSelected] = useState<Application | null>(null);

  const fetchApplications = async () => {
    let query = supabase
      .from('volunteer_applications')
      .select('*, volunteer_opportunities(title_en)')
      .order('created_at', { ascending: false });
    if (filter !== 'all') query = query.eq('status', filter);
    const { data } = await query;
    setApplications((data as Application[]) ?? []);
    setLoading(false);
  };

  useEffect(() => { fetchApplications(); }, [filter]);

  const updateStatus = async (id: string, status: string) => {
    await supabase.from('volunteer_applications').update({ status }).eq('id', id);
    setApplications(prev => prev.map(a => a.id === id ? { ...a, status } : a));
    if (selected?.id === id) setSelected(prev => prev ? { ...prev, status } : null);
  };

  const statusColor = (s: string) => {
    const map: Record<string, string> = {
      pending: 'bg-amber-50 text-amber-600',
      reviewed: 'bg-[#4ECDC4]/10 text-[#4ECDC4]',
      accepted: 'bg-green-100 text-green-700',
      rejected: 'bg-red-50 text-red-500',
    };
    return map[s] ?? 'bg-gray-100 text-gray-500';
  };

  return (
    <div className="flex min-h-screen bg-[#F5F7FA]">
      <AdminSidebar />
      <main className="flex-1 p-8 overflow-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#1B2838]">Volunteer Applications</h1>
          <p className="text-gray-500 text-sm mt-0.5">Review and manage volunteer applications</p>
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-5">
          {['all', ...STATUS_OPTIONS].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium cursor-pointer capitalize transition-colors whitespace-nowrap ${filter === f ? 'bg-[#1B2838] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'}`}
            >
              {f}
            </button>
          ))}
        </div>

        <div className="flex gap-5">
          <div className="flex-1 bg-white rounded-xl border border-gray-100 overflow-hidden">
            {loading ? (
              <div className="p-12 text-center text-gray-400">
                <div className="w-8 h-8 border-2 border-[#4ECDC4] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                Loading applications...
              </div>
            ) : applications.length === 0 ? (
              <div className="p-12 text-center text-gray-400">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3"><i className="ri-team-line text-3xl"></i></div>
                <p className="text-sm">No applications found</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Applicant</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Role</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {applications.map(app => (
                    <tr key={app.id} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => setSelected(app)}>
                      <td className="px-5 py-4">
                        <div className="text-sm font-medium text-[#1B2838]">{app.name}</div>
                        <div className="text-xs text-gray-400">{app.email}</div>
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-600">{app.volunteer_opportunities?.title_en ?? '—'}</td>
                      <td className="px-5 py-4">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${statusColor(app.status)}`}>{app.status}</span>
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-400">{new Date(app.created_at).toLocaleDateString()}</td>
                      <td className="px-5 py-4">
                        <select
                          value={app.status}
                          onChange={e => { e.stopPropagation(); updateStatus(app.id, e.target.value); }}
                          onClick={e => e.stopPropagation()}
                          className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-[#4ECDC4] cursor-pointer capitalize"
                        >
                          {STATUS_OPTIONS.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {selected && (
            <div className="w-80 bg-white rounded-xl border border-gray-100 p-6 flex-shrink-0">
              <div className="flex items-start justify-between mb-4">
                <h3 className="font-bold text-[#1B2838]">{selected.name}</h3>
                <button onClick={() => setSelected(null)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
                  <i className="ri-close-line text-gray-400 text-sm"></i>
                </button>
              </div>
              <div className="space-y-2 text-sm mb-4">
                <div className="flex gap-2"><span className="text-gray-400 w-16 flex-shrink-0">Email</span><a href={`mailto:${selected.email}`} className="text-[#4ECDC4] hover:underline truncate">{selected.email}</a></div>
                {selected.phone && <div className="flex gap-2"><span className="text-gray-400 w-16 flex-shrink-0">Phone</span><span className="text-gray-700">{selected.phone}</span></div>}
                <div className="flex gap-2"><span className="text-gray-400 w-16 flex-shrink-0">Role</span><span className="text-gray-700">{selected.volunteer_opportunities?.title_en ?? '—'}</span></div>
              </div>
              {selected.message && (
                <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600 leading-relaxed mb-4">{selected.message}</div>
              )}
              <div className="space-y-2">
                {STATUS_OPTIONS.map(s => (
                  <button
                    key={s}
                    onClick={() => updateStatus(selected.id, s)}
                    disabled={selected.status === s}
                    className={`w-full py-2 rounded-full text-xs font-medium cursor-pointer capitalize transition-colors whitespace-nowrap ${selected.status === s ? 'bg-[#1B2838] text-white' : 'border border-gray-200 text-gray-600 hover:bg-gray-50'} disabled:cursor-default`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
